import { useCryptoProvider } from './useCryptoProvider';
import { useCallback, useEffect, useState } from 'react';
import { usePaymentGateway } from './usePaymentGateway';
import usePaymentProviders from './usePaymentProviders';
import { PAYMENT_GATEWAY, PAYMENT_TRANSACTION_TYPE, type PaymentTransactionType } from '../../types/enums';
import { useBalances } from '../../queries';

export const usePaymentFlowState = () => {
  const [transactionType, setTransactionType] = useState<PaymentTransactionType>(PAYMENT_TRANSACTION_TYPE.DEPOSIT);
  const [paymentProvider, setPaymentProvider] = useState<string | null>(null);

  const { paymentGateway, paymentProviders } = usePaymentGateway(transactionType);
  const cryptoCashier = useCryptoProvider();
  const isCrypto = paymentGateway === PAYMENT_GATEWAY.CRYPTO;
  const requiresPaymentMethod = !isCrypto || cryptoCashier.requiresPaymentMethod;
  const filteredProviders = usePaymentProviders(paymentProviders ?? [], transactionType, paymentGateway);
  const { mainBalance } = useBalances();
  const hasBalance = !!mainBalance?.balance && Math.round(mainBalance.balance * 100) / 100 > 0;

  const isDeposit = transactionType === PAYMENT_TRANSACTION_TYPE.DEPOSIT;
  const isWithdrawal = transactionType === PAYMENT_TRANSACTION_TYPE.WITHDRAW;

  useEffect(() => {
    if (requiresPaymentMethod && !paymentProvider && filteredProviders.length === 1) {
      setPaymentProvider(filteredProviders[0].value);
    }
  }, [filteredProviders, paymentProvider, requiresPaymentMethod]);

  const handleTransactionTypeChange = useCallback((newType: PaymentTransactionType | null) => {
    if (newType !== null) {
      setPaymentProvider(null);
      setTransactionType(newType);
    }
  }, []);

  const handleProviderChange = useCallback((provider: string) => {
    setPaymentProvider(provider);
  }, []);

  const resolvedPaymentProvider =
    paymentProvider ?? (filteredProviders.length === 1 ? filteredProviders[0].value : null);
  const hasPaymentProviders = paymentProviders && paymentProviders.length > 0;

  const showPaymentProviderPicker = requiresPaymentMethod && !!hasPaymentProviders;
  const canShowPaymentForm = isCrypto
    ? !requiresPaymentMethod || !!resolvedPaymentProvider
    : !hasPaymentProviders || !!resolvedPaymentProvider;
  const providerWithdrawalUnavailable = isCrypto && !cryptoCashier.withdrawalEnabled;
  let withdrawalDisabledReason: 'provider_unavailable' | 'insufficient_balance' | null = null;
  if (providerWithdrawalUnavailable) withdrawalDisabledReason = 'provider_unavailable';
  else if (!hasBalance) withdrawalDisabledReason = 'insufficient_balance';
  const withdrawalDisabled = withdrawalDisabledReason !== null;

  return {
    cryptoCashier,
    showPaymentProviderPicker,
    canShowPaymentForm,
    withdrawalDisabled,
    transactionType,
    withdrawalDisabledReason,
    paymentProvider,
    resolvedPaymentProvider,
    paymentGateway,
    paymentProviders,
    filteredProviders,
    isDeposit,
    isWithdrawal,
    hasPaymentProviders,
    hasBalance,
    handleTransactionTypeChange,
    handleProviderChange,
    resetToDeposit: useCallback(() => {
      setPaymentProvider(null);
      setTransactionType(PAYMENT_TRANSACTION_TYPE.DEPOSIT);
    }, []),
  };
};
